<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="400px"
    >
      <v-card v-if="!deleteCheck" class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="property.id" class="text-h5">Update property</h2>
              <h2 v-else class="text-h5">Add a property</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <p
            v-if="errorMessage"
            id="error"
            class="error-message mt-2 text-start"
          >
            {{ errorMessage }}
          </p>
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="physical_address"
                v-slot="{ errors }"
                name="physical_address"
                rules="required"
              >
                <TextField
                  v-model.trim="property.physicalAddress"
                  :label="'Physical address*'"
                  :errors="errors"
                  :name="'physical_address'"
                  :type="'textarea'"
                />
              </ValidationProvider>

              <label class="text-subtitle-1">Property ownership*</label>
              <ValidationProvider
                id="sole_owner"
                v-slot="{ errors }"
                name="sole_owner"
                rules="required"
              >
                <v-radio-group
                  v-model="property.ownership"
                  :error-messages="errors"
                >
                  <v-radio
                    v-if="marriedInCommunityOrCustomary"
                    label="I co-own with my spouse"
                    value="CO_OWN_WITH_SPOUSE"
                    color="primary"
                  />
                  <v-radio
                    label="I am the sole owner"
                    value="SOLE_OWN"
                    color="primary"
                  />
                  <v-radio :label="coOwnText" value="CO_OWN" color="primary" />
                  <v-radio
                    v-if="marriedInCommunityOrCustomary"
                    label="My spouse is the sole owner"
                    value="SPOUSE_SOLE_OWNER"
                    color="primary"
                  />
                </v-radio-group>
              </ValidationProvider>

              <ValidationProvider
                v-if="isCoOwned"
                id="percentage_share"
                v-slot="{ errors }"
                name="percentage_share"
                rules="required|decimal|max_value:99"
              >
                <v-row no-gutters>
                  <v-col cols="12">
                    <label class="text-subtitle-1"
                      >What is your percentage share of the property?*</label
                    >
                  </v-col>
                  <v-col cols="12" class="mt-6">
                    <TextField
                      v-model.trim="property.percentageShare"
                      suffix="%"
                      :errors="errors"
                      :name="'percentage_share'"
                    />
                  </v-col>
                </v-row>
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col v-if="property.id" cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save'"
                  :small="true"
                  :callout="'right'"
                  :loading="buttonLoading"
                  @gradientButtonClicked="save"
                />
                <GradientButton
                  :title="'Remove Property'"
                  :small="true"
                  :fill="false"
                  @gradientButtonClicked="deleteCheck = true"
                />
              </div>
            </v-col>
            <v-col v-else cols="12">
              <div class="button-container">
                <GradientButton
                  :title="'Save And Continue'"
                  :small="true"
                  :callout="'right'"
                  :loading="buttonLoading"
                  @gradientButtonClicked="save"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>

      <DeleteDialog
        v-if="deleteCheck"
        :content="deleteDialogContent"
        @delete="deleteProperty"
        @dialogClosed="closeDialog"
      />
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
  </div>
</template>

<script>
  import {
    CREATE_PROPERTY_MUTATION,
    UPDATE_PROPERTY_MUTATION,
    DELETE_PROPERTY_MUTATION,
  } from '../../constants/graphql/properties';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import DeleteDialog from '../common/DeleteDialog.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'PropertyDialog',
    components: {
      DeleteDialog,
      ErrorSnackbar,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      propertyToUpdate: {
        type: Object,
        required: false,
        default: () => {
          return {};
        },
      },
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        property: {
          physicalAddress: null,
          ownership: null,
          percentageShare: null,
        },
        deleteCheck: false,
        errorMessage: null,
      };
    },
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      deleteDialogContent() {
        const text =
          'Are you sure you want to remove this property?<br><b>' +
          this.property.displayName +
          '</b><br><br>' +
          this.deleteDonationWarning;
        return {
          title: 'Remove property',
          text: text,
          buttonText: 'Yes, Remove Property',
        };
      },
      deleteDonationWarning() {
        if (this.property.specificDonation) {
          return '<b>Warning:</b> Donation of this asset to your desired beneficiaries will be deleted';
        } else {
          return '';
        }
      },
      marriedInCommunityOrCustomary() {
        return (
          this.testator.maritalRegime == 'IN_COMMUNITY' ||
          this.testator.maritalRegime == 'CUSTOMARY'
        );
      },
      coOwnText() {
        return this.marriedInCommunityOrCustomary
          ? 'I co-own with parties other than my spouse'
          : 'I co-own';
      },
      isCoOwned() {
        return (
          this.property.ownership == 'CO_OWN' ||
          this.property.ownership == 'CO_OWN_WITH_SPOUSE'
        );
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          this.property = JSON.parse(JSON.stringify(this.propertyToUpdate));
        }
      },
    },
    methods: {
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.property.id) {
              this.updateProperty();
            } else {
              this.createProperty();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      createProperty() {
        const propertyInputDetails = {
          testatorId: this.testatorId,
          physicalAddress: this.property.physicalAddress,
          ownership: this.property.ownership,
          percentageShare: parseFloat(this.property.percentageShare),
        };

        this.$apollo
          .mutate({
            mutation: CREATE_PROPERTY_MUTATION,
            variables: {
              propertyInputDetails,
            },
            update: (cache, { data: { createProperty } }) => {
              this.updateCacheAfterCreateProperty(cache, createProperty);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.$nextTick(() =>
                document
                  .getElementById('error')
                  .scrollIntoView({ block: 'center', behavior: 'smooth' })
              );
            } else {
              this.displayErrorAndCloseDialog();
              throw error;
            }
            this.buttonLoading = false;
          });
      },
      updateCacheAfterCreateProperty(cache, createProperty) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.properties.push(createProperty);

        cache.writeQuery({ ...query, data: data });
      },
      updateProperty() {
        const propertyInputDetails = {
          physicalAddress: this.property.physicalAddress,
          ownership: this.property.ownership,
          percentageShare: parseFloat(this.property.percentageShare),
        };
        const id = this.property.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_PROPERTY_MUTATION,
            variables: {
              id,
              propertyInputDetails,
            },
            update: (cache, { data: { updateProperty } }) => {
              this.updateCacheAfterUpdateProperty(cache, updateProperty);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.$nextTick(() =>
                document
                  .getElementById('error')
                  .scrollIntoView({ block: 'center', behavior: 'smooth' })
              );
            } else {
              this.displayErrorAndCloseDialog();
              throw error;
            }
            this.buttonLoading = false;
          });
      },
      updateCacheAfterUpdateProperty(cache, updateProperty) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var property = data.testator.properties.find(
          property => property.id == updateProperty.id
        );
        var property = updateProperty;

        cache.writeQuery({ ...query, data: data });
      },
      deleteProperty() {
        const id = this.property.id;

        this.$apollo
          .mutate({
            mutation: DELETE_PROPERTY_MUTATION,
            variables: {
              id,
            },
            update: (cache, { data: { deleteProperty } }) => {
              this.updateCacheAfterDeleteProperty(cache, deleteProperty);
            },
          })
          .then(_response => {
            this.closeDialog();
          })
          .catch(error => {
            this.displayErrorAndCloseDialog();
            throw error;
          });
      },
      updateCacheAfterDeleteProperty(cache, deleteProperty) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        var properties = data.testator.properties;
        var index = properties.findIndex(
          property => property.id == deleteProperty.id
        );
        properties.splice(index, 1);

        cache.writeQuery({ ...query, data: data });
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.property = {};
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
        this.deleteCheck = false;
        this.errorMessage = null;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .button-container {
    margin-bottom: 22px;
    * {
      margin-right: 15px;
    }
  }
</style>
